<template>
  <div>
    <c-modal
      classTitle="modal-title"
      title="Certificate"
      size="md"
      @close="$emit('close')"
    >
      <div v-if="activeStepIndex === 0">
        <div class="modal-body">
          <stepper
            :activeIndex="0"
            :steps="[{name: 'Verify License Info'}, {name: `Download Certificate`}]"
          />
          <br />

          <Notify>
            <div class="certificate-form-license-info">
              <h4>Licensure Info</h4>
              <p>
                Let's double check your license info as the information below will be displayed on your {{attendee.ce ? 'ce' : 'completion'}}
                certificate. If you need multiple certificates, simply come back here, update your information and
                then download the updated certificate.
              </p>
            </div>
          </Notify>
          <br />

          <license-info-form
            ref="licenseInfoRef"
            @change="changeField"
          />
        </div>

        <div class="modal-footer">
          <div class="certificate-form-buttons">
            <c-button
              uppercase
              fill
              theme="default"
              size="sm"
              icon="fa-close"
              @click="$emit('close')"
            >Close</c-button>

            <c-button
              theme="primary"
              size="sm"
              icon="fa-arrow-right"
              :show-indicator="showIndicator"
              uppercase
              fill
              @click="submit"
            >Next</c-button>
          </div>
        </div>
      </div>

      <div v-if="activeStepIndex === 1">
        <div class="modal-body">
          <stepper
            :activeIndex="1"
            :steps="[{name: 'Verify License Info'}, {name: `Download Certificate`}]"
          />
          <br />

          <Notify v-if="!certificateLinks?.mandated_certificate_link">
            <p>Use this button to download a single certificate for all your sessions attended.</p>
          </Notify>

          <br />
          <div style="text-align: center;">
            <c-button
              theme="success"
              size="sm"
              icon="fa-download"
              :show-indicator.sync="showCertificateButtonIndicator"
              uppercase
              fill
              @click="downloadCertificate(certificateLinks?.link)"
            >{{ downloadCertificateBtnTitle }}</c-button>
          </div>

          <div v-if="certificateLinks?.mandated_certificate_link" style="margin-top: 20px; text-align: center;">
            <c-button
              theme="success"
              size="sm"
              icon="fa-download"
              :show-indicator.sync="showCertificateButtonIndicator"
              uppercase
              fill
              @click="downloadCertificate(certificateLinks?.mandated_certificate_link)"
            >Download New York Mandated Certificate</c-button>
          </div>

          <evaluation-table-layout
            v-if="certificateLinks?.sessions?.length"
            title="Individual Sessions"
            :description="`Use these if you need ${certificateLinks?.approvals_abbr}`"
            style="margin-top: 20px;"
          >
            <div class="certificate-item" v-for="(item, index) in certificateLinks?.sessions" :key="`session-${index}`">
              <a href="#" @click="downloadCertificate(item.link)">
                <img src="/img/certificate-icon.svg" style="width: 30px; margin-right: 7px;" alt="Download Certificate" />
                {{item.name}}
              </a>
            </div>
          </evaluation-table-layout>
        </div>

        <div class="modal-footer">
          <div class="certificate-form-buttons">
            <c-button
              uppercase
              fill
              theme="default"
              size="sm"
              icon="fa-close"
              @click="$emit('close')"
            >Close</c-button>
          </div>
        </div>
      </div>
    </c-modal>
  </div>
</template>

<script>
import { attendeeFetchData } from "../../api";
import licenseAPI from "../../api/licenseAPI";
import Stepper from "../ui/Stepper";
import Notify from "../ui/Notify";
import LicenseInfoForm from "./license-info-form";
import {mapGetters} from 'vuex'
import EvaluationTableLayout from '../evaluations/evaluation-table-layout/index.vue'

export default {
  components: {
    EvaluationTableLayout,
    Stepper,
    Notify,
    LicenseInfoForm
  },
  data() {
    return {
      attendeeId: this.$route.params.attendeeId,
      showIndicator: false,
      activeStepIndex: 0,

      form: {
        licenseId: '',
        stateId: '',
        disciplineId: '',
        discipline: '',
        license: '',
      },

      certificate: {},
      showCertificateButtonIndicator: false,
      certificateLinks: {},
    }
  },
  computed: {
    ...mapGetters('attendeeStore', ['attendee', 'isSingleSession']),

    downloadCertificateBtnTitle() {
      let title = 'Download';

      if (this.isSingleSession) {
        if (this.certificateLinks?.mandated_certificate_link) {
          title = `${title} CE Certificate`;
        } else {
          title = `${title} Certificate`;
        }
      } else {
        title = 'All Sessions';
      }

      return title;
    },
  },
  methods: {
    // Returns a sanitized filename from attendee first/last name
    buildCertificateFileName() {
      const first = this.attendee?.first_name || '';
      const last = this.attendee?.last_name || '';
      const raw = `${first} ${last}`.trim() || 'certificate';
      return String(raw)
        .normalize('NFKD')
        .replace(/[^\w\s.-]/g, '') // remove illegal chars
        .replace(/\s+/g, ' ')      // collapse whitespace
        .trim()
        .replace(/\s/g, '_')       // spaces -> underscores
        .slice(0, 120);
    },

    downloadCertificate(url) {
      this.showCertificateButtonIndicator = true;

      attendeeFetchData(url, {
        responseType: 'blob'
      })
        .then((res) => {
          let blob = new Blob([res.data], {
            type: 'application/pdf'
          });

          const link = window.document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          const base = this.buildCertificateFileName();
          link.download = `${base}.pdf`;
          link.click();
        })
        .catch(() => {})
        .finally(()=>{
          this.showCertificateButtonIndicator = false;
        });
    },

    changeField(fields) {
      this.form = fields;
    },

    submit() {
      // old = licenseInfoRef
      this.$refs.licenseInfoRef.$refs.form.validate().then(success => {
        if (!success) {
          return;
        }

        this.showIndicator = true;

        const {stateId, disciplineId, discipline, license, licenseId} = this.form;

        const data = {
          state_id: stateId,
          discipline_id: disciplineId,
          license
        };

        if (discipline) {
          data.discipline_other = discipline;
        }

        licenseAPI.send(this.attendeeId, data)
          .then(() => {
            // this.activeStepIndex = 1;
            this.getCertificateLinks();
          })
          .catch(() => {})
          .finally(() => {
            // this.showIndicator = false;
          });

        this.$nextTick(() => {
          this.$refs.licenseInfoRef.$refs.form.reset();
        });
      });
    },

    getCertificateLinks() {
      licenseAPI.getCertificateLinks(this.attendeeId)
        .then((res) => {
          this.certificateLinks = res.data?.data;
          this.activeStepIndex = 1;
        })
        .catch(() => {})
        .finally(() => {
          this.showIndicator = false;
        });
    }
  },
}
</script>

<style lang="scss">

.modal-title{
  text-align: center;
}

.certificate-form-buttons {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.certificate-form-license-info {
  h4 {
    margin-bottom: var(--attendee-gap-md);
  }
}

.certificate-item {
  padding: 10px;

  &:not(:last-child) {
    border-bottom: 1px solid #ddd;
  }
}
</style>
